import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import LiveChat from '../components/landing-page/LiveChat';
import SubscriptionPaymentModal from '../components/SubscriptionPaymentModal';
import DemoFormModal from '../components/landing-page/DemoFormModal';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * /chat/:token — opened from the WhatsApp "Chat with Aayush" link.
 * Resolves the token to the cross-channel session and opens the rich, full-screen
 * Aayush widget resuming the SAME conversation/memory from where it was left off.
 */
export default function ChatResumePage() {
  const { token } = useParams();
  const { language, t } = useLanguage();
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(false);

  const [showDemoForm, setShowDemoForm] = useState(false);
  const [demoPrefill, setDemoPrefill] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentPrefill, setPaymentPrefill] = useState(null);
  const [paymentStartStep, setPaymentStartStep] = useState(2);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/live-chat/resolve-chat-link/${token}`);
        const data = await res.json();
        if (!active) return;
        if (data.success && data.session_id) setSessionId(data.session_id);
        else setError(true);
      } catch {
        if (active) setError(true);
      }
    })();
    return () => { active = false; };
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-green-50 flex items-center justify-center px-4" data-testid="chat-resume-page">
      {!sessionId && !error && (
        <div className="text-center" data-testid="chat-resume-loading">
          <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-white shadow-lg flex items-center justify-center">
            <i className="fa-solid fa-robot text-orange-500 text-2xl animate-pulse"></i>
          </div>
          <p className="text-gray-600 text-sm">आयुष आपकी बातचीत खोल रहे हैं…</p>
        </div>
      )}

      {error && (
        <div className="text-center max-w-sm" data-testid="chat-link-error">
          <p className="text-gray-700 font-medium mb-1">यह chat link अमान्य या expire हो गया है।</p>
          <p className="text-gray-500 text-sm mb-4">कृपया WhatsApp पर वापस जाकर दोबारा कोशिश करें, या नीचे से सीधे बात शुरू करें।</p>
          <a href="/" className="inline-block px-5 py-2.5 bg-orange-500 text-white rounded-xl text-sm font-bold hover:bg-orange-600 transition" data-testid="chat-link-home">
            शिक्षा मित्र Home
          </a>
        </div>
      )}

      {sessionId && (
        <LiveChat
          deepLinkSessionId={sessionId}
          forceFullScreen
          onOpenDemo={(prefill) => {
            setDemoPrefill(prefill || null);
            setShowDemoForm(true);
          }}
          onOpenPayment={(prefill) => {
            setPaymentPrefill(prefill || null);
            setPaymentStartStep(prefill?.startStep || 2);
            setShowPaymentModal(true);
          }}
        />
      )}

      <DemoFormModal
        isOpen={showDemoForm}
        onClose={() => { setShowDemoForm(false); setDemoPrefill(null); }}
        t={t}
        language={language}
        prefillData={demoPrefill}
        source={demoPrefill?.source || 'whatsapp_chat_link'}
      />

      <SubscriptionPaymentModal
        isOpen={showPaymentModal}
        onClose={() => { setShowPaymentModal(false); setPaymentPrefill(null); setPaymentStartStep(2); }}
        language={language}
        prefillData={paymentPrefill}
        startStep={paymentStartStep}
        source={paymentPrefill?.source || 'whatsapp_chat_link_payment'}
      />
    </div>
  );
}
